<template>
	<div id="qvo-app">
		<titlebar />
		<notificationCenter />
		<navbar />
		<main id="content" class="account">
			<h1>Account</h1>
			<tab-group class="account-body" v-model="tab">
				<tab-item title="My Profile" class="profile-team-comp">
					<profile :selectedUserId="loggedInUserId" :roles="roles" :clients="clients" :tab="tab" />
				</tab-item>
				<tab-item title="Team">
					<template v-if="(loggedInUserId != selectedUserId) && (!addingUser)">
						<back-nav-btn :action="backToTeam">Back to Team</back-nav-btn>
						<profile
							:selectedUserId="selectedUserId"
							:roles="roles"
							:clients="clients"
							:tab="tab"
							:backToTeam="backToTeam"
						/>
					</template>
					<template v-else-if="addingUser">
						<back-nav-btn :action="backToTeam">Back to Team</back-nav-btn>
						<profile
							:selectedUserId="''"
							:roles="roles"
							:clients="clients"
							:tab="tab"
							:backToTeam="backToTeam"
						/>
					</template>
					<template v-else>
						<team :user="loggedInUser" :hasClientOptions="hasClientOptions" @viewProfile="showProfile" @addUser="addUser"/>
					</template>
				</tab-item>
			</tab-group>
		</main>
		<footnote />
	</div>
</template>

<script>
	/**
	 * @fileoverview account.vue allows the users to navigate through the details of their profile and
	 * team accounts. It relies on vuex to map the state variables from the store files.
	 * 
	 * @components
	 * 		profile - contents of the profile tab 
	 *		team - contents of the team tab
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import profile from "@/components/profile.vue";
	import team from "@/components/team.vue";
	import { mapState } from "vuex";

	export default {
		name: "account",
		data() {
			return {
				selectedUser: {},	// user being displayed
				tab: "",	// tab selected
				selectedUserId: "",	// userId of the user being displayed
				addingUser: false	// true if adding a new user
			};
		},
		components: {
			profile,
			team,
		},
		computed: {
			...mapState({
				teamMembers: (state) => state.account.teamMembers,
				roles: (state) => state.account.roles,
				clients: (state) => state.account.clients,
				loggedInUser: (state) => state.account.loggedInUser,
				loggedInUserId: (state) => state.account.loggedInUserId,
				activeTenantId: (state) => state.account.activeTenantId,
			}),
			/**
			 * The user has client options if they have more than one client
			 * @returns true if they have more than one client, false otherwise
			 */
			hasClientOptions() {
				return this.clients.length > 1;
			}
		},
		methods: {
			/**
			 * Display the selected profile by retriving the user and adjusting the tab if necessary
			 * @param {string} userId - id of the user to display
			 */
			showProfile(userId) {
				this.selectedUserId = userId;
				this.getUser(userId);

				if (this.loggedInUserId == this.selectedUserId) {
					this.tab = "my-profile";
				}
			},
			/**
			 * Set the selected user by retrieving the user data from the teamMembers list
			 * @param {string} userId - id of the user to retrieve
			 */
			getUser(userId) {
				// create a local copy of the selected user for carefree editing
				let target = this.teamMembers.find(
					(user) => user.userId === userId
				);
				let tempUser = {
					userId: target.userId,
					name: target.name,
					emailAddress: target.emailAddress,
					accessRole: target.accessRole,
					isAllowedAllClients: target.isAllowedAllClients,
					allowedClients: target.allowedClients,
					isDisabled: target.isDisabled,
				};
				this.selectedUser = tempUser;
			},
			/**
			 * Set the addingUser flag to true
			 */
			addUser() {
				this.addingUser = true;
			},
			/**
			 * Return to the team view. Reset flags.
			 */
			backToTeam() {
				this.selectedUser = this.loggedInUser;
				this.selectedUserId = this.loggedInUserId;
				this.addingUser = false;
			},
		},
		/**
		 * Created lifecycle hook
		 * Retrieve the team members based on the active tenantId
		 */
		created() {
			// retreving data from database at created stage of lifecycle
			this.selectedUser = this.loggedInUser;
			this.selectedUserId = this.loggedInUserId;
			this.$store.dispatch("setTeamMembers", this.activeTenantId);
		},
	};
</script>

<!-- Page-Specific Style Import (don't edit here) -->
<style lang="scss">
	@import "@/design/views/account.scss";
</style>

